import Layout from './core/Layout';
import Router from './core/Router';
import pageMeta from './pageMeta.json';
import { trackPageView } from '../analytics';

import IndexPage from './pages/IndexPage';
import ActividadesPage from './pages/ActividadesPage';
import ContactoPage from './pages/ContactoPage';
import CookiesPage from './pages/CookiesPage';
import Covid19Page from './pages/Covid19Page';
import GaleriaPage from './pages/GaleriaPage';
import HorariosPage from './pages/HorariosPage';
import LegalPage from './pages/LegalPage';
import TablonPage from './pages/TablonPage';
import RedesSocialesPage from './pages/RedesSocialesPage';
import TerapiasPage from './pages/TerapiasPage';
import VideosPage from './pages/VideosPage';
import CuotasPage from './pages/CuotasPage';

export default class App {
  constructor(options = {}) {
    this.pageMeta = options.pageMeta || pageMeta;
    this.pageDefinitions = this.createPageDefinitions();
    this.pageDefinitionMap = this.createPageDefinitionMap();
    this.fileRoutes = this.createFileRoutes();
    this.activePageIds = new Set(
      options.activePageIds || [
        'index',
        // 'covid19',
        'actividades',
        'horarios',
        'galeria',
        'videos',
        'tablon',
        'redes-sociales',
        'terapias',
        'contacto',
      ]
    );
    this.alwaysAvailablePageIds = new Set(options.alwaysAvailablePageIds || ['cookies', 'legal', 'cuotas']);
    this.defaultRoute = this.resolveDefaultRoute();
    this.layout = null;
    this.router = null;
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  createPageDefinitions() {
    return [
      { id: 'index', label: 'INICIO', PageClass: IndexPage },
      { id: 'covid19', label: 'COVID19', PageClass: Covid19Page },
      { id: 'actividades', label: 'ACTIVIDADES', PageClass: ActividadesPage },
      { id: 'horarios', label: 'HORARIOS', PageClass: HorariosPage },
      { id: 'galeria', label: 'GALERIA', PageClass: GaleriaPage },
      { id: 'videos', label: 'VIDEOS', PageClass: VideosPage },
      { id: 'tablon', label: 'TABLON', PageClass: TablonPage },
      { id: 'redes-sociales', label: 'REDES SOCIALES', PageClass: RedesSocialesPage },
      { id: 'terapias', label: 'TERAPIAS', PageClass: TerapiasPage },
      { id: 'contacto', label: 'CONTACTO', alignRight: true, PageClass: ContactoPage },
      { id: 'cookies', PageClass: CookiesPage },
      { id: 'legal', PageClass: LegalPage },
      { id: 'cuotas', PageClass: CuotasPage },
    ];
  }

  createPageDefinitionMap() {
    return this.pageDefinitions.reduce((definitions, definition) => {
      definitions[definition.id] = definition;
      return definitions;
    }, {});
  }

  createFileRoutes() {
    return this.pageDefinitions.reduce((routes, definition) => {
      const aliases = definition.fileAliases || this.createDefaultFileAliases(definition.id);
      aliases.forEach((alias) => {
        routes[alias] = definition.id;
      });
      return routes;
    }, {});
  }

  createDefaultFileAliases(routeId) {
    if (routeId === 'index') {
      return ['index.html', 'index.php'];
    }
    return [`${routeId}.html`, `${routeId}.php`];
  }

  isActivePage(routeId) {
    return this.activePageIds.has(routeId);
  }

  isNavigableRoute(routeId) {
    return this.isActivePage(routeId) || this.alwaysAvailablePageIds.has(routeId);
  }

  resolveDefaultRoute() {
    if (this.isNavigableRoute('index')) {
      return 'index';
    }

    const firstNavigablePage = this.pageDefinitions.find((definition) => this.isNavigableRoute(definition.id));
    return firstNavigablePage ? firstNavigablePage.id : null;
  }

  getMenuItems() {
    return this.pageDefinitions
      .filter((definition) => this.isActivePage(definition.id) && definition.label)
      .map((definition) => ({
        route: definition.id,
        label: definition.label,
        alignRight: Boolean(definition.alignRight),
      }));
  }

  buildRoutes() {
    return this.pageDefinitions.reduce((routes, definition) => {
      if (!this.isNavigableRoute(definition.id)) {
        return routes;
      }

      routes[definition.id] = new definition.PageClass(this.pageMeta[definition.id] || {});
      return routes;
    }, {});
  }

  start() {
    const menuContainer = document.getElementById('menu_principal');
    const content = document.getElementById('spa_page_content');
    if (!menuContainer || !content || !this.defaultRoute) {
      return;
    }

    this.layout = new Layout(menuContainer, this.getMenuItems());
    this.layout.renderMenu();

    const pages = this.buildRoutes();
    this.router = new Router({
      routes: pages,
      defaultRoute: this.defaultRoute,
      context: {
        content,
        layout: this.layout,
      },
      onRouteChange: (routeId) => {
        this.layout.setActiveRoute(this.layout.hasRoute(routeId) ? routeId : null);
        this.layout.closeMenu();
        trackPageView(routeId);
      },
    });

    document.addEventListener('click', this.handleDocumentClick);
    this.router.start();
  }

  stop() {
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.router) {
      this.router.stop();
    }
  }

  handleDocumentClick(event) {
    if (!this.router || event.defaultPrevented) {
      return;
    }

    const anchor = event.target.closest('a[href]');
    if (!anchor) {
      return;
    }
    if (anchor.target && anchor.target.toLowerCase() === '_blank') {
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href) {
      return;
    }

    const cleanHref = href.trim();
    if (!cleanHref || cleanHref === '#' || this.isExternalHref(cleanHref)) {
      return;
    }

    const route = this.normalizeHashRoute(cleanHref) || this.normalizeFileRoute(cleanHref);
    if (!route) {
      return;
    }

    event.preventDefault();

    if (!this.isNavigableRoute(route)) {
      this.router.navigate(this.defaultRoute);
      return;
    }

    this.router.navigate(route);
  }

  isExternalHref(href) {
    return /^(https?:|mailto:|tel:|intent:|javascript:|data:)/i.test(href);
  }

  normalizeHashRoute(href) {
    if (!href.startsWith('#/')) {
      return null;
    }

    const route = href.replace(/^#\/?/, '').split('/')[0].trim();
    if (!route || !this.pageDefinitionMap[route]) {
      return null;
    }

    return route;
  }

  normalizeFileRoute(href) {
    const cleanHref = href.split('?')[0].split('#')[0].replace(/^\.?\//, '').toLowerCase();
    return this.fileRoutes[cleanHref] || null;
  }
}

export function startSpaApp() {
  const app = new App();
  app.start();
  return app;
}
